import { Funnel } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import type { AgencyPortalUser } from "../types";
import { reFunnelSearchHref, relativeTime } from "./pendingSignups";

/**
 * REF-143: people who confirmed their e-mail on the real-estate portal but
 * never created their agency. Each links to the RE funnel tab, searched by
 * their e-mail, for the timeline and replay links.
 */
export const PendingSignupsSection = ({
  users,
  hiddenInternal,
}: {
  users: AgencyPortalUser[];
  hiddenInternal: number;
}) => {
  if (users.length === 0 && hiddenInternal === 0) return null;
  return (
    <Card data-testid="pending-signups">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          Pending signups
          <Badge variant="secondary">{users.length}</Badge>
        </CardTitle>
        <CardDescription>
          Signed up on the portal and confirmed their e-mail, but have not
          created their agency yet.
          {hiddenInternal > 0
            ? ` ${hiddenInternal} staff login${hiddenInternal === 1 ? " is" : "s are"} hidden — turn off “Hide internal” to see ${hiddenInternal === 1 ? "it" : "them"}.`
            : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        {users.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Only staff logins are pending.
          </p>
        ) : (
          <ul className="flex max-h-72 flex-col overflow-y-auto">
            {users.map((user) => (
              <li
                key={user.id}
                className="flex flex-wrap items-center gap-2 border-b py-2 text-sm last:border-b-0"
              >
                <span className="min-w-0 truncate">{user.email}</span>
                {user.display_name ? (
                  <span className="truncate text-muted-foreground">
                    {user.display_name}
                  </span>
                ) : null}
                <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
                  <span
                    title={
                      user.created_at
                        ? new Date(user.created_at).toLocaleString()
                        : undefined
                    }
                  >
                    signed up{" "}
                    {user.created_at
                      ? new Date(user.created_at).toLocaleDateString()
                      : "—"}
                  </span>
                  <span>
                    {user.last_login_at
                      ? `last sign-in ${relativeTime(user.last_login_at)}`
                      : "never signed in"}
                  </span>
                  <Link
                    to={reFunnelSearchHref(user.email)}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    title="Open this lead in the RE funnel tab"
                  >
                    <Funnel className="size-3" aria-hidden="true" />
                    RE funnel
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
